import {
  Box,
  CloseButton,
  Divider,
  Flex,
  Link,
  Text,
  useColorModeValue,
  BoxProps,
  // eslint-disable-next-line no-restricted-imports
  Icon as ChakraIcon,
} from '@chakra-ui/react';
import { HelpCircle } from 'lucide-react';
import { SidebarAccountSection } from './SidebarAccountSection';
import { SidebarCollapsibleItem } from './SidebarCollapsibleItem';
import { SidebarCustomerSwitcher } from './SidebarCustomerSwitcher';
import { SidebarModelSelect } from './SidebarModelSelect';
import { getAllRoutes } from 'src/app/routes/utils';
import { YapamarLogo } from 'src/components/atoms/YapamarLogo';
import { useActiveRoute } from 'src/hooks/useActiveRoute';
import { useInboxUnreadCountQuery } from 'src/api/inboxes';
import { useAppContext } from 'src/contexts/AppContext';
import { IS_DEV } from 'src/constants';

const HELP_URL = 'https://help.yapamar.com';

type SidebarContentProps = BoxProps & {
  onClose?: () => void;
};

const SidebarHelpItem = () => (
  <Link
    href={HELP_URL}
    isExternal
    style={{ textDecoration: 'none' }}
    display="block">
    <Flex
      align="center"
      mx="4"
      px="3"
      py="2"
      borderRadius="lg"
      cursor="pointer"
      color="sidebar.text"
      _hover={{ bg: 'sidebar.itemHoverBg' }}>
      <ChakraIcon
        as={HelpCircle}
        mr="3"
        w={5}
        h={5}
        color="sidebar.textMuted"
      />
      <Text fontFamily="marketingPage" fontSize={15} fontWeight="medium">
        Help
      </Text>
    </Flex>
  </Link>
);

export const SidebarContent = ({ onClose, ...rest }: SidebarContentProps) => {
  const { activeRoute } = useActiveRoute();
  const { currentCustomer } = useAppContext();
  const logoTheme = useColorModeValue('dark', 'white');
  const { data: unreadCount } = useInboxUnreadCountQuery(
    currentCustomer?.customerName ?? '',
  );

  return (
    <Box
      transition="3s ease"
      bg="surface.sidebar"
      w={{ base: 'full', lg: '280px' }}
      pos="fixed"
      overflow="hidden"
      h="full"
      zIndex="docked"
      {...rest}>
      <Flex direction="column" h="full">
        <Flex h="20" alignItems="center" mx="4" justifyContent="space-between">
          <YapamarLogo theme={logoTheme} />
          {onClose && (
            <CloseButton
              display={{ base: 'flex', lg: 'none' }}
              onClick={onClose}
            />
          )}
        </Flex>
        <SidebarModelSelect />
        <Box flex="1" minH={0} overflowY="auto" overflowX="hidden" pb="2">
          {getAllRoutes()
            .filter((route) => IS_DEV || !route.isDevOnly)
            .map((route) => {
              const isRouteActive = activeRoute?.url === route.url;
              const isInboxRoute = route.url === '/inbox';
              return (
                <SidebarCollapsibleItem
                  key={route.url}
                  icon={route.icon}
                  route={route}
                  isActive={isRouteActive}
                  badgeCount={isInboxRoute ? unreadCount : undefined}
                  onNavigate={onClose}
                />
              );
            })}
        </Box>
        <Box flexShrink={0} pb="4">
          <Divider borderColor="surface.border" mb="2" />
          <SidebarAccountSection onNavigate={onClose} />
          <SidebarHelpItem />
          <SidebarCustomerSwitcher />
        </Box>
      </Flex>
    </Box>
  );
};
